import _throwOnUpdateScalar from 'rlink-collections/_throwOnUpdateScalar';
import op from 'rlink-collections/operations';

function isSelf (path) {
  return path.length === 0;
}

const DEBUG_UPDATE_AT = false;

const NonExistence = Symbol('NonExistence');

export default function updateAt (initialObj, initialOps, coveredPath = [], onUpdateScalar = _throwOnUpdateScalar) {
  DEBUG_UPDATE_AT && console.log('updateAt', {initialObj, initialOps, coveredPath})
  let obj = initialObj;
  let ops = initialOps;
  let addValues = [];
  let sortOp = null;
  let sliceIdx = 0;
  for (let [idx, [path, operation, value]] of ops.entries()) {
    if (isSelf(path)) {
      // self op
      if (operation === op.add) {
        addValues.push(value);
        sliceIdx = idx + 1;
      } else if (addValues.length > 0 ) {
        // we can add values in a batch but have to apply them
        // before moving on
        break;
      } else if (operation === op.sort) {
        sortOp = value;
        sliceIdx = idx + 1;
      } else if (sortOp) {
        // as with adding values, sort ops can override each-other
        // but the sort should be applied before doing anything else
        break;
      } else if (operation === op.set) {
        // setting self means subsequent operations happen on the new
        // value and previous ops are meaningless
        obj = value;
        sliceIdx = idx + 1;
        addValues = [];
        sortOp = null;
      } else if (operation === op.rem) {
        // similar with removing self
        obj = NonExistence;
        sliceIdx = idx + 1;
        addValues = [];
        sortOp = null;
      } else {
        throw new Error(`Unkown operation: ${operation}`);
      }
    }
  }
  if (sliceIdx > 0) {
    ops = initialOps.slice(sliceIdx);
  }
  if (addValues.length > 0) {
    if (!this.isIterable(obj)) {
      // we will operate on keys form here on, so we need an indexable
      // collection to move on
      return onUpdateScalar(obj, ops, coveredPath);
    }
    let newObj = this.copyGenerator(obj,
      function *(yieldable, entries) {
        for (let [key, value] of entries) {
          yield yieldable(key, value);
        }
        yield* addValues;
      }
    );
    return this.updateAt(newObj, ops, coveredPath, onUpdateScalar);
  }
  if (sortOp) {
    return this.updateAt(
      this.sorted(obj, sortOp), ops, coveredPath, onUpdateScalar);
  }

  // at this point ops contains no empty paths - they each affect at least
  // one key - but for each key they cannot affect each-other, so it is
  // safe to batch them by key

  // we might be done!
  if (ops.length === 0) {
    // unless we are trying to rem self which is an invalid operation
    // unless we have been called from within a key setting operation
    if (obj === NonExistence && coveredPath.length === 0) {
      throw new Error('Cannot remove self from existence.');
    } else {
      return obj;
    }
  }

  if (!this.isIterable(obj)) {
    // we will operate on keys form here on, so we need an indexable
    // collection to move on
    return onUpdateScalar(obj, ops, coveredPath);
  }

  let keyOperations = new Map();
  for (let [[key, ...remaingPath], operation, value] of ops) {
    if (!keyOperations.has(key)) {
      keyOperations.set(key, []);
    }
    keyOperations.get(key).push([remaingPath, operation, value]);
  }
  let collections = this;
  DEBUG_UPDATE_AT && console.groupCollapsed('Generating a copy of', obj);
  let newObj = this.copyGenerator(obj,
    function *(yieldable, entries) {
      for (let [key, value] of entries) {
        DEBUG_UPDATE_AT && console.group('==', key,':', value);
        if (keyOperations.has(key)) {
          let keyValue = collections.updateAt(value, keyOperations.get(key),
            coveredPath.concat([key]), onUpdateScalar);
          DEBUG_UPDATE_AT && console.log('--', key, 'assigned changed value', keyValue);
          if (keyValue !== NonExistence) {
            yield yieldable(key, keyValue);
          }
          // we're done resolving this key
          keyOperations.delete(key);
        } else {
          DEBUG_UPDATE_AT && console.log('--', key, 'using existing value', value);
          yield yieldable(key, value);
        }
        DEBUG_UPDATE_AT && console.groupEnd();
      }
      // set any remaining keys
      for (let [key, opsForKeyValue] of keyOperations.entries()) {
        DEBUG_UPDATE_AT && console.group('@@', key,':', opsForKeyValue);
        let keyValue = collections.updateAt(NonExistence, opsForKeyValue,
            coveredPath.concat([key]), onUpdateScalar);
        DEBUG_UPDATE_AT && console.log('--', key, 'assigning additional value', keyValue);
        if (keyValue !== NonExistence) {
          yield yieldable(key, keyValue);
        }
        DEBUG_UPDATE_AT && console.groupEnd();
      }
    }
  );
  DEBUG_UPDATE_AT && console.log("FINAL NEW OBJECT", newObj);
  DEBUG_UPDATE_AT && console.groupEnd();
  return newObj;
}




// WEBPACK FOOTER //
// ./lib/rlink-collections/core/updateAt.js